<template>
  <div>
    <v-alert v-if="errore" type="error" text dense class="mb-4">
      Non è stato possibile caricare i colori.
      <v-btn small text color="error" class="ml-2" @click="carica">Riprova</v-btn>
    </v-alert>

    <div v-if="caricamento" class="py-8 d-flex justify-center">
      <v-progress-circular indeterminate color="green" />
    </div>

    <template v-else>
      <!-- Intestazione: una colonna per consegna, più il totale prenotato -->
      <div v-if="intestazione" class="riga intestazione" :style="stile_griglia">
        <div class="cella_nome grey--text d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small class="mr-2" v-on="on" @click="alterna_tutto">
                <v-icon small>
                  {{ tutto_chiuso ? "mdi-expand-all" : "mdi-collapse-all" }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ tutto_chiuso ? "Espandi tutto" : "Comprimi tutto" }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                class="mr-2"
                :color="mostra_disponibile ? 'blue' : undefined"
                v-on="on"
                @click="mostra_disponibile = !mostra_disponibile"
              >
                <v-icon small>mdi-package-variant-closed</v-icon>
              </v-btn>
            </template>
            <span>
              {{ mostra_disponibile ? "Nascondi" : "Mostra" }} la disponibilità
            </span>
          </v-tooltip>
          Varietà
        </div>
        <div v-for="consegna in consegne" :key="consegna.id" class="cella_testata">
          <slot name="testata-consegna" :consegna="consegna">
            <span class="text-caption">{{ giorno(consegna.ts_data) }}</span>
          </slot>
        </div>
        <div v-if="mostra_disponibile" class="cella_testata grey--text text-caption">
          Disponibile
        </div>
        <div class="cella_testata grey--text text-caption">Prenotato</div>
      </div>

      <div v-if="!gruppi.length" class="py-8 text-center grey--text">
        Nessun colore prenotato.
        <span v-if="!mostra_tutto">Attiva “mostra tutto” per vedere il catalogo.</span>
      </div>

      <div
        v-for="(gruppo, indice) in gruppi"
        :key="gruppo.diametro"
        class="mb-2"
      >
        <!-- Diametro -->
        <div
          class="riga gruppo"
          :class="{ separato: indice > 0 }"
          :style="stile_griglia"
          @click="alterna(diametri_chiusi, gruppo.diametro)"
        >
          <div class="cella_nome d-flex align-center">
            <v-icon small class="mr-2">
              {{ chiuso(diametri_chiusi, gruppo.diametro) ? "mdi-chevron-right" : "mdi-chevron-down" }}
            </v-icon>
            <v-icon small class="mr-2">mdi-bucket-outline</v-icon>
            <span class="font-italic grey--text">Vaso Ø {{ gruppo.diametro }}</span>
          </div>
          <div v-for="consegna in consegne" :key="consegna.id" class="cella_testata grey--text">
            {{ totale_gruppo(gruppo, consegna.id) || "" }}
          </div>
          <div v-if="mostra_disponibile" class="cella_testata"></div>
          <div class="cella_testata grey--text">{{ totale_gruppo(gruppo, null) || "" }}</div>
        </div>

        <template v-if="!chiuso(diametri_chiusi, gruppo.diametro)">
          <div v-for="prodotto in gruppo.prodotti" :key="prodotto.id">
            <!-- Prodotto -->
            <div
              class="riga prodotto"
              :style="stile_griglia"
              @click="alterna(prodotti_chiusi, prodotto.id)"
            >
              <div class="cella_nome d-flex align-center">
                <v-icon small class="mr-2">
                  {{ chiuso(prodotti_chiusi, prodotto.id) ? "mdi-chevron-right" : "mdi-chevron-down" }}
                </v-icon>
                <span>{{ nome_breve(prodotto) }}</span>
                <v-chip
                  v-if="specie_di(prodotto)"
                  x-small
                  class="ml-3"
                  color="green lighten-5"
                >
                  <v-icon x-small left color="green">mdi-sprout</v-icon>
                  {{ specie_di(prodotto).nome }}
                </v-chip>
                <v-chip v-else x-small class="ml-3" color="amber lighten-4">
                  <v-icon x-small left color="amber darken-2">mdi-alert-outline</v-icon>
                  senza specie
                </v-chip>
              </div>
              <div v-for="consegna in consegne" :key="consegna.id" class="cella_testata">
                {{ totale_prodotto(prodotto, consegna.id) || "" }}
              </div>
              <div v-if="mostra_disponibile" class="cella_testata"></div>
              <div class="cella_testata font-weight-medium">
                {{ totale_prodotto(prodotto, null) || "" }}
              </div>
            </div>

            <!-- La serie sta sulla riga, non come intestazione: molte
                 serie hanno una varietà sola e i raggruppamenti facevano
                 più rumore che struttura. -->
            <template v-if="!chiuso(prodotti_chiusi, prodotto.id)">
              <v-lazy
                v-for="colore in prodotto.colori"
                :key="colore.id"
                :min-height="52"
                :options="{ threshold: 0.2 }"
                transition="fade-transition"
              >
                <div
                  class="riga colore"
                  :style="stile_griglia"
                  :class="{
                    evidenziato: evidenziata(prodotto, colore),
                    completo: completo(prodotto.id, colore.id),
                  }"
                >
                  <div class="cella_nome d-flex align-center">
                    <v-avatar size="34" class="mr-3" :color="colore.orfano ? 'grey lighten-2' : undefined">
                      <v-img v-if="colore.img" :src="colore.img" />
                      <v-icon v-else small>mdi-help</v-icon>
                    </v-avatar>
                    <!-- Serie e varietà fanno da qualificatori, il colore è
                         l'aggancio. Nel legacy la voce È il colore: senza
                         specie non c'è nulla da anteporre. -->
                    <span v-if="colore.nome_serie" class="tenue mr-2">
                      {{ colore.nome_serie }}
                    </span>
                    <span v-if="colore.tipo === 'varieta'" class="tenue mr-2">
                      {{ colore.nome }}
                    </span>
                    <span class="nome_colore">
                      {{ colore.nome_colore || colore.nome }}
                    </span>
                    <v-chip
                      v-if="colore.tipo !== 'varieta' && prodotto.specie"
                      x-small
                      class="ml-2"
                      color="amber lighten-4"
                    >
                      legacy
                    </v-chip>
                    <!-- Esclusa dal prodotto ma presente nei dati: una
                         stagione in cui era prodotta, poi dismessa. -->
                    <v-chip
                      v-if="colore.esclusa"
                      x-small
                      class="ml-2"
                      color="grey lighten-3"
                    >
                      dismessa
                    </v-chip>
                    <NotaColore
                      :valore="nota_di(prodotto, colore)"
                      @salva="imposta_nota(prodotto, colore, $event)"
                    />
                    <span
                      v-if="nota_di(prodotto, colore)"
                      class="ml-1 grey--text font-italic text-caption nota"
                      :title="nota_di(prodotto, colore)"
                    >
                      {{ nota_di(prodotto, colore) }}
                    </span>
                  </div>

                  <!-- Una cella per consegna -->
                  <div v-for="consegna in consegne" :key="consegna.id" class="cella_qta">
                    <CellaQta
                      :valore="qta(prodotto.id, colore.id, consegna.id)"
                      :disabilitata="bloccata(prodotto.id, colore.id, consegna.id)"
                      @cambia="imposta(prodotto, colore, $event, consegna.id)"
                    />
                  </div>

                  <!-- Quanto resta da promettere agli altri clienti -->
                  <div
                    v-if="mostra_disponibile"
                    class="cella_testata disponibile"
                    :class="classe_disponibile(prodotto.id, colore.id)"
                  >
                    <template v-if="disponibile(prodotto.id, colore.id) !== null">
                      {{ disponibile(prodotto.id, colore.id) }}
                    </template>
                    <span v-else class="grey--text text--lighten-1">—</span>
                  </div>

                  <!-- Totale prenotato, con la rimanenza da distribuire -->
                  <div class="cella_qta">
                    <CellaQta
                      bloccabile
                      enfasi
                      :valore="qta(prodotto.id, colore.id, null)"
                      :prefisso="prefisso_rimanenza(prodotto.id, colore.id)"
                      :errore="rimanenza(prodotto.id, colore.id) < 0"
                      :successo="completo(prodotto.id, colore.id)"
                      @cambia="imposta(prodotto, colore, $event, null)"
                    />
                  </div>
                </div>
              </v-lazy>
            </template>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import resolver from "@/service/varieta_resolver";
import CellaQta from "./CellaQta.vue";

const PREF_DISPONIBILE = "cris_mostra_disponibile";
import NotaColore from "./NotaColore.vue";

/**
 * Griglia delle quantità di una prenotazione crisantemi.
 *
 * Gerarchia: diametro → prodotto → colore. Le consegne sono colonne.
 *
 * Il layout è una CSS grid con un template calcolato in un solo punto
 * (`stile_griglia`), condiviso da tutte le righe: il numero di consegne non
 * entra in nessuna aritmetica di offset, quindi non esiste un limite al
 * numero di colonne.
 */
export default {
  components: { CellaQta, NotaColore },

  props: {
    prenotazione: { type: Object, required: true },
    // Mostra tutto il catalogo invece dei soli colori con quantità.
    mostraTutto: { type: Boolean, default: false },
    // Voce su cui saltare, arrivando dalla disponibilità. Serve anche il
    // prodotto: una varietà è condivisa da tutti i prodotti della specie,
    // quindi il solo id combacerebbe su ogni riga omonima.
    voceEvidenziata: { type: String, default: null },
    prodottoEvidenziato: { type: String, default: null },
    // Insieme di prodotti da mostrare. Se omesso usa i crisantemi del
    // catalogo; il dettaglio lo valorizza per la sezione "altri prodotti".
    prodotti: { type: Array, default: null },
    // Da spegnere quando la griglia segue un'altra che ha già la sua
    // intestazione: le colonne sono le stesse e ripeterla è rumore.
    intestazione: { type: Boolean, default: true },
  },

  data() {
    return {
      colori_per_prodotto: {},
      caricamento: true,
      errore: false,
      diametri_chiusi: [],
      prodotti_chiusi: [],
      // Spento di default: durante la consultazione normale è una colonna
      // di numeri in più che non serve.
      mostra_disponibile: localStorage.getItem(PREF_DISPONIBILE) === "true",
    };
  },

  computed: {
    consegne() {
      return this.prenotazione.consegne || [];
    },

    stile_griglia() {
      // repeat() non ammette 0: con zero consegne la colonna ripetuta va
      // omessa del tutto, altrimenti l'intera dichiarazione è invalida e la
      // griglia collassa su una sola colonna.
      const colonne = ["minmax(240px, 1.6fr)"];
      if (this.consegne.length) {
        colonne.push(`repeat(${this.consegne.length}, minmax(84px, 104px))`);
      }
      // Disponibile, poi prenotato: si legge quanto resta e poi si scrive.
      if (this.mostra_disponibile) colonne.push("90px");
      colonne.push("160px");
      return { gridTemplateColumns: colonne.join(" ") };
    },

    prodotti_in_griglia() {
      if (this.prodotti) return this.prodotti;
      return this.$store.getters.get_lista_prodotti_raw.filter(
        (p) => p.sotto_categoria == "Crisantemo"
      );
    },

    /**
     * Identità dell'insieme di prodotti, non del suo riferimento: la lista
     * può arrivare da un computed che si ricalcola a ogni aggiornamento
     * della prenotazione, e ricaricare i colori ogni volta farebbe lampeggiare
     * la griglia senza motivo.
     */
    chiave_prodotti() {
      return this.prodotti_in_griglia
        .map((p) => p.id)
        .sort()
        .join(",");
    },

    tutto_chiuso() {
      return (
        this.gruppi.length > 0 &&
        this.diametri_chiusi.length >= this.gruppi.length
      );
    },

    gruppi() {
      const per_diametro = _.groupBy(this.prodotti_in_griglia, "diametro");

      return Object.keys(per_diametro)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((diametro) => ({
          diametro,
          prodotti: per_diametro[diametro]
            .map((prodotto) => ({
              ...prodotto,
              colori: this.colori_visibili(prodotto),
            }))
            .filter((prodotto) => prodotto.colori.length),
        }))
        .filter((gruppo) => gruppo.prodotti.length);
    },
  },

  watch: {
    // Il catalogo può arrivare dopo il primo render, e la lista può crescere
    // quando si aggiunge un prodotto.
    chiave_prodotti: "carica",

    mostra_disponibile(valore) {
      localStorage.setItem(PREF_DISPONIBILE, valore);
    },
  },

  mounted() {
    this.carica();
  },

  methods: {
    async carica() {
      this.errore = false;
      // Lo spinner solo al primo caricamento: sui successivi la griglia è già
      // a schermo e sostituirla con uno spinner sarebbe un passo indietro.
      this.caricamento = !Object.keys(this.colori_per_prodotto).length;
      try {
        const mappa = await resolver.carica_voci_prodotti(
          this.prodotti_in_griglia
        );
        const oggetto = {};
        mappa.forEach((colori, id) => {
          oggetto[id] = colori;
        });
        this.colori_per_prodotto = oggetto;
      } catch {
        this.errore = true;
      } finally {
        this.caricamento = false;
      }
    },

    /** Id che compaiono nei dati, anche se la sorgente non li elenca. */
    ids_nei_dati(id_prodotto) {
      const ids = [];
      const raccogli = (contenitore) => {
        const prodotto = (contenitore.prodotti || []).find(
          (p) => p.id_prodotto == id_prodotto
        );
        if (!prodotto) return;
        (prodotto.colori || []).forEach((c) =>
          ids.push(String(c.id_varieta || c.id_colore || c.id))
        );
      };
      raccogli(this.prenotazione);
      this.consegne.forEach(raccogli);
      return ids;
    },

    colori_visibili(prodotto) {
      const base = this.colori_per_prodotto[prodotto.id] || [];
      const presenti = this.ids_nei_dati(prodotto.id);
      const tutti = resolver.unisci_voci_presenti(prodotto, base, presenti);
      if (this.mostraTutto) return tutti;
      const con_dato = new Set(presenti);
      return tutti.filter((c) => con_dato.has(c.id));
    },

    /* Le letture bastano dell'id; la scrittura ha bisogno anche del tipo,
       perche' decide sotto quale chiave finisce il riferimento. */

    /**
     * Una cella di consegna si blocca solo se non c'è niente da distribuire
     * *e* la cella è vuota.
     *
     * Bloccare al solo azzerarsi del prenotato lasciava le quantità già
     * distribuite inaccessibili: restavano nella consegna, mandavano la
     * rimanenza in negativo e non c'era modo di correggerle. Il blocco serve
     * a non distribuire ciò che non è prenotato, non a congelare un dato che
     * esiste già.
     */
    bloccata(id_prodotto, id_voce, id_consegna) {
      if (this.qta(id_prodotto, id_voce, null)) return false;
      return !this.qta(id_prodotto, id_voce, id_consegna);
    },

    disponibile(id_prodotto, id_voce) {
      return this.$store.getters["crisantemi/disponibile"]({
        id_prodotto,
        id_voce,
      });
    },

    classe_disponibile(id_prodotto, id_voce) {
      const valore = this.disponibile(id_prodotto, id_voce);
      if (valore === null) return "";
      if (valore < 0) return "red--text font-weight-bold";
      if (valore === 0) return "orange--text text--darken-2";
      return "grey--text text--darken-1";
    },

    evidenziata(prodotto, voce) {
      return (
        !!this.voceEvidenziata &&
        voce.id === this.voceEvidenziata &&
        prodotto.id === this.prodottoEvidenziato
      );
    },

    qta(id_prodotto, id_voce, id_consegna) {
      return this.$store.getters["crisantemi/qta"]({
        id_prenotazione: this.prenotazione.id,
        id_consegna,
        id_prodotto,
        id_voce,
      });
    },

    rimanenza(id_prodotto, id_voce) {
      return this.$store.getters["crisantemi/rimanenza"]({
        id_prenotazione: this.prenotazione.id,
        id_prodotto,
        id_voce,
      });
    },

    /**
     * Prenotato interamente ripartito sulle consegne.
     *
     * Richiede che ci sia qualcosa di prenotato: senza questa guardia una
     * riga a zero avrebbe rimanenza zero e risulterebbe "completata" pur non
     * contenendo nulla — visibile appena si mostra tutto il catalogo.
     */
    completo(id_prodotto, id_voce) {
      if (!this.consegne.length) return false;
      if (!this.qta(id_prodotto, id_voce, null)) return false;
      return this.rimanenza(id_prodotto, id_voce) === 0;
    },

    prefisso_rimanenza(id_prodotto, id_voce) {
      if (!this.consegne.length) return undefined;
      return this.rimanenza(id_prodotto, id_voce) + " / ";
    },

    imposta(prodotto, voce, qta, id_consegna) {
      this.$store.dispatch("crisantemi/imposta_voce", {
        id_prenotazione: this.prenotazione.id,
        id_consegna,
        id_prodotto: prodotto.id,
        id_voce: voce.id,
        tipo: voce.tipo,
        qta,
      });
    },

    // La nota vive sul totale prenotato, non sulla singola consegna.
    imposta_nota(prodotto, voce, nota) {
      this.$store.dispatch("crisantemi/imposta_voce", {
        id_prenotazione: this.prenotazione.id,
        id_consegna: null,
        id_prodotto: prodotto.id,
        id_voce: voce.id,
        tipo: voce.tipo,
        nota,
      });
    },

    nota_di(prodotto, colore) {
      const p = (this.prenotazione.prodotti || []).find(
        (x) => x.id_prodotto == prodotto.id
      );
      if (!p) return null;
      const c = (p.colori || []).find(
        (x) => String(x.id_colore || x.id) === colore.id
      );
      return c ? c.nota : null;
    },

    totale_prodotto(prodotto, id_consegna) {
      return prodotto.colori.reduce(
        (tot, colore) => tot + this.qta(prodotto.id, colore.id, id_consegna),
        0
      );
    },

    totale_gruppo(gruppo, id_consegna) {
      return gruppo.prodotti.reduce(
        (tot, prodotto) => tot + this.totale_prodotto(prodotto, id_consegna),
        0
      );
    },

    /** Specie collegata al prodotto, se assegnata. */
    specie_di(prodotto) {
      if (!prodotto.specie) return null;
      return this.$store.getters.get_specie_by_id(prodotto.specie) || null;
    },

    nome_breve(prodotto) {
      return prodotto.nome.split(" in ")[0];
    },

    giorno(ts) {
      return ts ? moment.unix(ts).format("DD/MM") : "—";
    },

    chiuso(lista, id) {
      return lista.includes(id);
    },

    alterna(lista, id) {
      const i = lista.indexOf(id);
      if (i === -1) lista.push(id);
      else lista.splice(i, 1);
    },

    comprimi_tutto() {
      this.diametri_chiusi = this.gruppi.map((g) => g.diametro);
    },

    /**
     * Riaprendo azzera anche i prodotti chiusi: altrimenti "espandi tutto"
     * lascerebbe richiusi quelli piegati a mano, che è il contrario di
     * quello che il comando promette.
     */
    espandi_tutto() {
      this.diametri_chiusi = [];
      this.prodotti_chiusi = [];
    },

    /**
     * Il comando vale per l'intero dettaglio, non per la sola griglia che
     * ospita il bottone: l'intenzione viene emessa perché chi ci sta sopra
     * la propaghi alle altre griglie.
     */
    alterna_tutto() {
      const chiudere = !this.tutto_chiuso;
      if (chiudere) this.comprimi_tutto();
      else this.espandi_tutto();
      this.$emit("alterna-tutto", chiudere);
    },
  },
};
</script>

<style scoped>
/* Il padding orizzontale sta sulla riga base: le colonne della grid nascono
   dal content box, quindi righe con padding diversi si disallineerebbero. */
.riga {
  display: grid;
  align-items: center;
  column-gap: 8px;
  padding-left: 8px;
  padding-right: 8px;
}

.intestazione {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  padding-top: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.gruppo,
.prodotto {
  cursor: pointer;
  padding-top: 6px;
  padding-bottom: 6px;
}

/* Il filetto separa i gruppi tra loro: sul primo si sommerebbe al bordo
   dell'intestazione, o al divisore che apre la sezione. */
.gruppo.separato {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 8px;
}

.gruppo:hover {
  background: rgba(0, 0, 0, 0.02);
}

/* Il prodotto è l'intestazione dei colori che lo seguono: senza uno stacco
   proprio, nome prodotto e nomi colore si leggono come un elenco unico. */
.prodotto {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  margin-top: 10px;
}

.prodotto:hover {
  background: rgba(0, 0, 0, 0.07);
}

.colore {
  padding-top: 4px;
  padding-bottom: 4px;
}

.colore:hover {
  background: rgba(0, 0, 0, 0.03);
}

/* Riga interamente ripartita. Se quasi tutte lo diventano, il verde è lo
   sfondo normale e a risaltare restano le righe bianche: cioè il lavoro
   ancora da fare, che è quello che serve trovare in fretta. */
.colore.completo {
  background: rgba(76, 175, 80, 0.07);
}

.colore.completo:hover {
  background: rgba(76, 175, 80, 0.14);
}

.colore.evidenziato,
.colore.evidenziato:hover {
  background: rgba(255, 193, 7, 0.22);
}


.cella_nome {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* La nota non deve spingere fuori il resto della riga: si tronca e
   il testo intero resta nel title. */
.tenue {
  color: rgba(0, 0, 0, 0.55);
}

.nome_colore {
  font-weight: 500;
}

.nota {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cella_testata,
.cella_qta {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* I numeri delle celle sono rientrati dal padding interno del campo (8px in
   CellaQta); i totali di riga sarebbero a filo del bordo colonna. Con le
   cornici invisibili lo sfalsamento si nota, quindi lo si compensa qui. */
.cella_testata {
  padding-right: 14px;
}

/* L'ultima colonna ha il lucchetto fuori dal campo: 8px di margine più 18px
   di icona che sottraggono larghezza al campo, e quindi al suo numero. */
.cella_testata:last-child {
  padding-right: 38px;
}

.prodotto .cella_nome {
  padding-left: 12px;
  font-weight: 500;
  font-size: 1.02rem;
}

.colore .cella_nome {
  padding-left: 28px;
}
</style>
